import React, { useState, useEffect } from "react";
import { useFormikContext } from "formik";
import { Button } from "components/atoms";
import {
  DatePickerInput,
  DocumentUploadModal,
  RadioButtonGroup,
  TextInput,
  MultiChoiceCheckboxGroup,
  MultiChoiceRadioButtonGroup,
} from "components/molecules";
import { useUserData, useUpload, useFileValidation } from "hooks";
import { documentService } from "services";
import {
  oshcTypeOptions,
  disabilityOptions,
  ApplicationFormValues,
  FileWithType,
} from "types";

interface AppServicesSectionProps {
  currentSubStage?: string;
}

const ALLOWED_KEYS: Record<string, Set<string>> = {
  extractedData: new Set([
    "personalInformation",
    "englishLanguageTestScores",
    "highSchool",
    "tertiaryEducation1",
    "tertiaryEducation2",
    "tertiaryEducation3",
    "educationDetails",
    "scholarshipFinancialSupport",
    "healthInsurance",
    "studentSupportServices",
    "employment1",
    "employment2",
    "employment3",
    "employmentDetails",
  ]),
};

const AppServicesSection: React.FC<AppServicesSectionProps> = ({
  currentSubStage = "financial",
}) => {
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    setFieldTouched,
  } = useFormikContext<ApplicationFormValues>();

  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [uploadedDocuments, setUploadedDocuments] = useState<{
    bankStatements?: { id: string; fileName: string };
    loanDocuments?: { id: string; fileName: string };
    scholarshipDocuments?: { id: string; fileName: string };
    sponsorIdentity?: { id: string; fileName: string };
    relationshipEvidence?: { id: string; fileName: string };
    propertyDeed?: { id: string; fileName: string };
    businessRegistration?: { id: string; fileName: string };
    incomeTax?: { id: string; fileName: string };
    healthInsuranceEvidence?: { id: string; fileName: string };
    dependentPassports?: { id: string; fileName: string };
  }>({});
  const userDataHook = useUserData();
  const { userData } = userDataHook;

  const { validateFileSync, getAcceptedFileTypes, getMaxFileSize } =
    useFileValidation({
      maxFileSize: 5 * 1024 * 1024, // 5MB
      enableMagicNumberValidation: false, // Disabled for sync validation
      sanitizeMetadata: true,
    });

  const {
    stage,
    uploadProgress,
    successMessage,
    errorMessage,
    handleFileUpload,
    handleFileRemove,
    handleError,
  } = useUpload(userDataHook, ALLOWED_KEYS, {
    enableExtraction: false,
    customUploadMessage: "Documents uploaded successfully.",
  });

  const servicesTypes = [
    "BANK_STATEMENTS",
    "SCHOLARSHIP_DOCUMENTS",
    "SPONSOR_IDENTITY",
    "RELATIONSHIP_EVIDENCE",
    "PROPERTY_DEED",
    "BUSINESS_REGISTRATION_CERTIFICATE",
    "INCOME_TAX",
    "HEALTH_INSURANCE_EVIDENCE",
    "DEPENDENT_PASSPORT",
  ];

  useEffect(() => {
    if (userData?.id) {
      documentService
        .fetchDocumentsWithMapping(servicesTypes)
        .then(setUploadedDocuments);
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.id]);

  const getDocumentFields = (filterByStage?: string) => {
    const needsFinancialDocs =
      values.payingMoreThan50Percent === true &&
      values.hasScholarshipSponsorship === false;

    const hasScholarshipSponsorship = values.hasScholarshipSponsorship === true;
    const isOrgSponsor = isOrganizationalSponsor();

    const allDocuments = [
      {
        id: "bankstatements",
        label: "Bank Statements",
        description: "Recent bank statements showing money deposits",
        accept: ".pdf,.jpg,.jpeg,.doc,.docx",
        required: needsFinancialDocs,
        uploaded: !!uploadedDocuments.bankStatements,
        stage: "financial",
      },
      {
        id: "loandocuments",
        label: "Loan Documents",
        description:
          "Approved loan documents from financial institution or government",
        accept: ".pdf,.jpg,.jpeg,.doc,.docx",
        required: needsFinancialDocs,
        uploaded: !!uploadedDocuments.loanDocuments,
        stage: "financial",
      },
      {
        id: "scholarshipdocuments",
        label: "Scholarship/Grant Documents",
        description: "Official scholarship or financial support award letter",
        accept: ".pdf,.jpg,.jpeg,.doc,.docx",
        required: hasScholarshipSponsorship,
        uploaded: !!uploadedDocuments.scholarshipDocuments,
        stage: "financial",
      },
      {
        id: "sponsoridentity",
        label: "Sponsor Identity Documents",
        description: isOrgSponsor
          ? "Official registration/incorporation documents of sponsoring organization"
          : "ID documents of person providing financial support",
        accept: ".pdf,.jpg,.jpeg,.doc,.docx",
        required: hasScholarshipSponsorship && !!isOrgSponsor,
        uploaded: !!uploadedDocuments.sponsorIdentity,
        stage: "financial",
      },
      {
        id: "relationshipevidence",
        label: "Relationship Evidence",
        description:
          "Proof of relationship with financial sponsor (birth certificate, marriage certificate, etc.)",
        accept: ".pdf,.jpg,.jpeg,.doc,.docx",
        required: hasScholarshipSponsorship && !!isOrgSponsor,
        uploaded: !!uploadedDocuments.relationshipEvidence,
        stage: "financial",
      },
      {
        id: "propertydeed",
        label: "Property Deed",
        description: "Property ownership deed or title document",
        accept: ".pdf,.jpg,.jpeg,.doc,.docx",
        required: needsFinancialDocs,
        uploaded: !!uploadedDocuments.propertyDeed,
        stage: "financial",
      },
      {
        id: "businessregistration",
        label: "Business Registration Certificate",
        description:
          "Business registration certificate or incorporation documents",
        accept: ".pdf,.jpg,.jpeg,.doc,.docx",
        required: needsFinancialDocs,
        uploaded: !!uploadedDocuments.businessRegistration,
        stage: "financial",
      },
      {
        id: "incometax",
        label: "Income Tax Return",
        description: "Recent income tax return or assessment notice",
        accept: ".pdf,.jpg,.jpeg",
        required: needsFinancialDocs,
        uploaded: !!uploadedDocuments.incomeTax,
        stage: "financial",
      },
      {
        id: "healthinsuranceevidence",
        label: "OSHC Policy Documents",
        description: "Copy of current OSHC policy certificate and details",
        accept: ".pdf,.jpg,.jpeg,.doc,.docx",
        required: values.hasCurrentOSHC === true,
        uploaded: !!uploadedDocuments.healthInsuranceEvidence,
        stage: "health",
      },
      {
        id: "dependentpassport",
        label: "Additional Dependant's Passports",
        description: "Additional member passports for family/couple OSHC",
        accept: ".pdf,.jpg,.jpeg,.doc,.docx",
        required: !!(
          values.wantUniversityToArrangeOSHC === true &&
          (values.oshcCoverageType === "couple" ||
            values.oshcCoverageType === "family")
        ),
        uploaded: !!uploadedDocuments.dependentPassports,
        stage: "health",
      },
    ];

    if (filterByStage) {
      return allDocuments.filter(
        (doc) => doc.stage === filterByStage && doc.required,
      );
    }

    return allDocuments;
  };

  const handleUploadWithRefresh = async (files: FileWithType[]) => {
    try {
      await handleFileUpload(files);
      setTimeout(async () => {
        if (userData?.id) {
          const docs =
            await documentService.fetchDocumentsWithMapping(servicesTypes);

          setUploadedDocuments(docs);
        }
      }, 1000);
      return { files, results: [] };
    } catch (error) {
      console.error("Upload failed:", error);
      return { files: [], results: [] };
    }
  };

  const isOrganizationalSponsor = () => {
    const sponsorName = values.scholarshipProviderName?.toLowerCase() || "";

    const orgIndicators = [
      "university",
      "college",
      "institute",
      "foundation",
      "government",
      "ministry",
      "department",
      "council",
      "agency",
      "corporation",
      "company",
      "ltd",
      "inc",
      "llc",
      "org",
      "association",
      "society",
      "trust",
      "fund",
      "scholarship",
      "grant",
      "program",
      "scheme",
      "bureau",
      "commission",
    ];

    return orgIndicators.some((indicator) => sponsorName.includes(indicator));
  };

  const renderScholarshipFinancialSection = () => (
    <div className="student-services-section">
      <div className="form-section">
        <div className="section-header">
          <h4>Scholarship and Financial</h4>
        </div>
        <div className="form-section">
          <RadioButtonGroup
            name="hasScholarshipSponsorship"
            label="Have you been granted or are you intending to apply for a scholarship or sponsorship? *"
            value={values.hasScholarshipSponsorship}
            onChange={(value) => {
              setFieldValue("hasScholarshipSponsorship", value);
              setFieldTouched("hasScholarshipSponsorship", true);

              if (value === false) {
                setFieldValue("scholarshipProviderName", "");
                setFieldValue("allowSponsorProgressSharing", null);
              }
            }}
          />
          {values.hasScholarshipSponsorship && (
            <>
              <TextInput
                id="scholarshipProviderName"
                label="Scholarship/sponsor provider name *"
                placeholder="Enter your scholarship/sponsor provider"
                getFieldProps={() => ({
                  value: values.scholarshipProviderName,
                  onChange: handleChange,
                  onBlur: handleBlur,
                })}
                error={errors.scholarshipProviderName}
                touched={touched.scholarshipProviderName}
              />
              <RadioButtonGroup
                name="allowSponsorProgressSharing"
                label="If sponsored by government/private organisation, do you give permission to provide academic progress information to your sponsor? *"
                value={values.allowSponsorProgressSharing}
                onChange={(value) => {
                  setFieldValue("allowSponsorProgressSharing", value);
                  setFieldTouched("allowSponsorProgressSharing", true);
                }}
              />
            </>
          )}

          <RadioButtonGroup
            name="payingMoreThan50Percent"
            label="Will you be paying more than 50% of your course fees before you start? *"
            value={values.payingMoreThan50Percent}
            onChange={(value) => {
              setFieldValue("payingMoreThan50Percent", value);
              setFieldTouched("payingMoreThan50Percent", true);
            }}
          />

          {documentService.countRequiredDocs(
            getDocumentFields(currentSubStage),
          ) > 0 && (
            <div className="documents-section">
              <div className="upload-header">
                <Button
                  type="button"
                  text="Upload Financial Documents"
                  onClick={() => setShowDocumentModal(true)}
                  variant="border"
                  width="12rem"
                />
                <div className="docs-counter">
                  <span className="uploaded-count">
                    {documentService.countUploadedDocs(
                      getDocumentFields(currentSubStage),
                    )}
                  </span>
                  <span className="separator">/</span>
                  <span className="total-count">
                    {documentService.countRequiredDocs(
                      getDocumentFields(currentSubStage),
                    )}
                  </span>
                  <span className="docs-label">documents</span>
                </div>
              </div>
              <div className="upload-help-text">
                <p>
                  <strong>Evidence you can provide includes:</strong>
                </p>
                <ul>
                  <li>Money deposits held within a financial institution</li>
                  <li>Loans from a financial institution and/or government</li>
                  <li>Scholarship or financial support documentation</li>
                  <li>
                    If someone else is providing funds: evidence of relationship
                    and their identity documents
                  </li>
                  <li>
                    If providing deposit evidence: explanation of fund sources
                  </li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>

      <DocumentUploadModal
        isOpen={showDocumentModal}
        onClose={() => {
          handleError("");
          setShowDocumentModal(false);
        }}
        title="Upload Financial Documents"
        description="Please attach evidence of financial support and guarantees:"
        documents={getDocumentFields(currentSubStage)}
        onFileUpload={handleUploadWithRefresh}
        onFileRemove={handleFileRemove}
        validateFile={validateFileSync}
        acceptedFileTypes={getAcceptedFileTypes()}
        onError={handleError}
        errorMessage={errorMessage}
        successMessage={successMessage}
        maxFileSize={getMaxFileSize()}
        stage={stage}
        uploadProgress={uploadProgress}
      />
    </div>
  );

  const renderHealthInsuranceSection = () => (
    <div className="student-services-section">
      <div className="form-section">
        <div className="section-header">
          <h4>Health Insurance</h4>
        </div>
        <div className="form-section">
          <RadioButtonGroup
            name="hasCurrentOSHC"
            label="Do you currently hold Overseas Student Health Cover (OSHC)? *"
            value={values.hasCurrentOSHC}
            onChange={(value) => {
              setFieldValue("hasCurrentOSHC", value);
              setFieldTouched("hasCurrentOSHC", true);

              if (value === true) {
                setFieldValue("wantUniversityToArrangeOSHC", null);
                setFieldValue("oshcCoverageType", "");
              }

              if (value === false) {
                setFieldValue("oshcProviderName", "");
                setFieldValue("oshcMembershipNumber", "");
                setFieldValue("oshcExpiryDate", null);
              }
            }}
          />
          {values.hasCurrentOSHC === true && (
            <>
              <TextInput
                id="oshcProviderName"
                label="OSHC provider name *"
                placeholder="Enter your OSHC provider"
                getFieldProps={() => ({
                  value: values.oshcProviderName,
                  onChange: handleChange,
                  onBlur: handleBlur,
                })}
                error={errors.oshcProviderName}
                touched={touched.oshcProviderName}
              />
              <TextInput
                id="oshcMembershipNumber"
                label="Membership number *"
                placeholder="Enter your membership number "
                getFieldProps={() => ({
                  value: values.oshcMembershipNumber,
                  onChange: handleChange,
                  onBlur: handleBlur,
                })}
                error={errors.oshcMembershipNumber}
                touched={touched.oshcMembershipNumber}
              />

              <div className="oshc-expiry-date-container">
                <DatePickerInput
                  id="oshcExpiryDate"
                  className="oshcExpiryDate"
                  label="Expiry date *"
                  value={values.oshcExpiryDate as Date | null}
                  onChange={(date) => setFieldValue("oshcExpiryDate", date)}
                  preset="futureDate"
                />
              </div>
            </>
          )}
          {values.hasCurrentOSHC === false && (
            <>
              <RadioButtonGroup
                name="wantUniversityToArrangeOSHC"
                label="Do you want the university to arrange health cover for you? *"
                value={values.wantUniversityToArrangeOSHC}
                onChange={(value) => {
                  setFieldValue("wantUniversityToArrangeOSHC", value);
                  setFieldTouched("wantUniversityToArrangeOSHC", true);

                  if (value === false) {
                    setFieldValue("oshcCoverageType", null);
                  }
                }}
              />
              {values.wantUniversityToArrangeOSHC && (
                <MultiChoiceRadioButtonGroup
                  name="oshcCoverageType"
                  label="Coverage type required *"
                  options={oshcTypeOptions}
                  value={values.oshcCoverageType as string}
                  onChange={(value) => {
                    setFieldValue("oshcCoverageType", value);
                    setFieldTouched("oshcCoverageType", true);
                  }}
                  otherPlaceholder="Enter coverage type required."
                />
              )}
            </>
          )}

          {documentService.countRequiredDocs(
            getDocumentFields(currentSubStage),
          ) > 0 && (
            <div className="documents-section">
              <div className="upload-header">
                <Button
                  type="button"
                  text="Upload Health Documents"
                  onClick={() => setShowDocumentModal(true)}
                  variant="border"
                  width="12rem"
                />
                <div className="docs-counter">
                  <span className="uploaded-count">
                    {documentService.countUploadedDocs(
                      getDocumentFields(currentSubStage),
                    )}
                  </span>
                  <span className="separator">/</span>
                  <span className="total-count">
                    {documentService.countRequiredDocs(
                      getDocumentFields(currentSubStage),
                    )}
                  </span>
                  <span className="docs-label">documents</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <DocumentUploadModal
        isOpen={showDocumentModal}
        onClose={() => {
          handleError("");
          setShowDocumentModal(false);
        }}
        title="Upload Health Insurance Documents"
        description="Please attach evidence of health insurance:"
        documents={getDocumentFields(currentSubStage)}
        onFileUpload={handleUploadWithRefresh}
        onFileRemove={handleFileRemove}
        validateFile={validateFileSync}
        acceptedFileTypes={getAcceptedFileTypes()}
        onError={handleError}
        errorMessage={errorMessage}
        successMessage={successMessage}
        maxFileSize={getMaxFileSize()}
        stage={stage}
        uploadProgress={uploadProgress}
      />
    </div>
  );

  const renderStudentSupporSection = () => (
    <div className="student-services-section">
      <div className="form-section">
        <div className="section-header">
          <h4>Student Support</h4>
        </div>

        <div className="form-section">
          <RadioButtonGroup
            name="hasDisabilityMedicalCondition"
            label="Do you have a disability, impairment or long-term medical condition that may affect your studies? *"
            value={values.hasDisabilityMedicalCondition}
            onChange={(value) => {
              setFieldValue("hasDisabilityMedicalCondition", value);
              setFieldTouched("hasDisabilityMedicalCondition", true);
            }}
          />
          {values.hasDisabilityMedicalCondition && (
            <>
              <MultiChoiceCheckboxGroup
                name="disabilityType"
                label="Please identify the nature of your condition"
                options={disabilityOptions}
                value={values.disabilityType as string[]}
                otherValue={values.otherDetails as string}
                onChange={(values) => setFieldValue("disabilityType", values)}
                onOtherChange={(value) => setFieldValue("otherDetails", value)}
                showOther={true}
                otherPlaceholder="Please specify"
                required={true}
              />
              <div className="disability-start-date-container">
                <DatePickerInput
                  id="disabilityStartDate"
                  className="disabilityStartDate"
                  label="Start Date *"
                  value={values.disabilityStartDate as Date | null}
                  onChange={(date) =>
                    setFieldValue("disabilityStartDate", date)
                  }
                  preset="pastDate"
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  switch (currentSubStage) {
    case "financial":
      return renderScholarshipFinancialSection();
    case "health":
      return renderHealthInsuranceSection();
    case "support":
      return renderStudentSupporSection();
    default:
      return renderScholarshipFinancialSection();
  }
};

export default AppServicesSection;
